import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO, SERVICES } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";
import ContactForm from "../components/ContactForm";

export default function Industrigulve() {
  const serviceData = SERVICES.find((s) => s.slug === "industrigulve");
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images if CMS fetch fails
  const fallbackImages = [
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00001.jpeg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00002.jpeg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00003.jpeg"
  ];

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/by-slug/da/industrigulve");
        if (res.ok) {
          const data = await res.json();
          setGallery(data);
        }
      } catch (err) {
        console.error("Fejl ved hentning af galleri fra CMS:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  const galleryImages = gallery?.images || fallbackImages;
  const galleryTitle = gallery?.title?.da || "Industrigulve";
  const galleryDesc = gallery?.description?.da || serviceData.shortDesc;

  return (
    <>
      <Helmet>
        <title>Industrigulve Halsnæs | Slidstærke & Skridsikre Belægninger</title>
        <meta
          name="description"
          content="Professionelle industrigulve i Halsnæs og omegn. Slidstærke, kemikalieresistente og skridsikre gulve til lagerhaller, værksteder og produktion."
        />
        <meta property="og:title" content="Industrigulve Halsnæs | Slidstærke & Skridsikre Belægninger" />
        <meta
          property="og:description"
          content="Professionelle industrigulve i Halsnæs og omegn. Slidstærke, kemikalieresistente og skridsikre gulve til lagerhaller, værksteder og produktion."
        />
        <meta property="og:image" content={fallbackImages[0]} />
      </Helmet>

      {/* Page Header */}
      <section className="bg-brand-blue-900 text-white pt-48 pb-28 sm:pt-44 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={fallbackImages[0]}
            alt="Industrigulv baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-blue-300 mb-3">
              Ydelser / Gulvløsninger
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Tungt Belastbare Industrigulve
            </h1>
            <p className="text-lg text-brand-blue-100 mt-4 max-w-2xl">
              Fugefrie gulvløsninger designet til hård mekanisk belastning, skridsikkerhed og strenge hygiejnekrav.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Detailed Copy */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-blue-900 mb-4">
                  Gulvløsninger til industri og erhverv
                </h2>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Industrigulve skal kunne modstå helt andre belastninger end almindelige gulve. Tung trafik fra gaffeltrucks, palleløftere og maskiner, kombineret med risiko for spild af kemikalier, olier og varme væsker, stiller enorme krav til gulvets beskaffenhed og styrke.
                </p>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Hos Halsnæs Epoxygulve har vi stor erfaring med at rådgive om og udføre industrigulve, der overholder alle gældende standarder og lovkrav. Vi arbejder med højkvalitets epoxy- og polyurethan-betonbelægninger, der skaber et sikkert, hygiejnisk og ekstremt holdbart arbejdsmiljø.
                </p>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Fordele ved vores industrigulve
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {serviceData.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-blue-600 mt-1 text-sm"></i>
                      <span className="text-sm font-semibold text-concrete-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Sikkerhed og skridsikring i højsædet
                </h3>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Et sikkert arbejdsmiljø er afgørende. Vi kan indbygge præcis den skridsikring (fra R9 til R13), der passer til din virksomheds behov. Det sker ved at indstrø kvartssand i belægningen, hvilket giver en ru overflade, der forhindrer skridulykker – selv når gulvet er vådt eller fedtet af olie.
                </p>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Hygiejniske og rengøringsvenlige
                </h3>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Da vores industrigulve er 100% fugefrie, er der ingen sprækker eller fuger, hvor snavs, bakterier og svamp kan samle sig. Det gør gulvene særdeles velegnede til fødevareindustrien, medicinalvirksomheder, storkøkkener og laboratorier, hvor der stilles strenge krav til hygiejne og nem rengøring.
                </p>
              </div>

              {/* Dynamic CMS Gallery Section */}
              <div className="pt-8 border-t border-concrete-100">
                <h3 className="text-2xl font-bold text-brand-blue-900 mb-2">
                  Galleri: {galleryTitle}
                </h3>
                <p className="text-sm text-concrete-500 mb-6">
                  {galleryDesc}
                </p>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-blue-600"></i>
                  </div>
                ) : (
                  <GalleryLightbox images={galleryImages} title={galleryTitle} />
                )}
              </div>
            </div>

            {/* Right: Sidebar with Contact Form */}
            <div className="lg:col-span-5 w-full lg:sticky lg:top-28">
              <ContactForm source="Ydelsesside: Industrigulve" />
              
              {/* Quick Contact Box */}
              <div className="mt-6 rounded-2xl bg-concrete-50 p-6 border border-concrete-100 text-center space-y-4">
                <h4 className="font-bold text-brand-blue-900">Har du spørgsmål?</h4>
                <p className="text-xs text-concrete-500">
                  Kontakt Simon Babiel direkte for rådgivning om industrigulve.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href={`tel:+45${CONTACT_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-phone"></i>
                    Tlf: {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-envelope"></i>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-industrial.jpg"
            alt="Slidstærkt industrigulv"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Få professionel rådgivning om dit industrigulv
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Vi kommer gerne ud til en uforpligtende besigtigelse af dine lokaler, så vi kan vurdere undergulvets tilstand og give dig den helt rigtige pris.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
            >
              Få et tilbud
            </Link>
            <a
              href={`tel:+45${CONTACT_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
            >
              <i className="fa-solid fa-phone"></i>
              Ring til Simon
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
